import { useEffect, useMemo, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  completeTask,
  getDayPlan,
  getGoalTree,
  listChecklist,
  listTasks,
  moveBlock,
  updateChecklistItem,
  type ChecklistItem,
  type DayHabit,
  type Task,
} from '../api';
import { useAccountDay } from '../accountDay';
import { shiftDay, localDay } from '../timezones';
import { focusNow, upNext, extendedMinutes, RAIL_HORIZON_MS, type NowBlock } from '../nowBlock';
import { goalChipsFor } from '../goalFocus';
import { goalColorMap } from '../colors';
import { inlineKeys } from '../keys';
import { useKeys } from '../keyScope';
import { isTypingTarget } from '../keyboard';
import { hasOpenSurface } from '../useDismiss';
import { QueryState } from './QueryState';
import { STATUS_VERB } from '../taskVocab';

/**
 * The doing screen.
 *
 * §1's loop is capture → organise → plan → **execute** → reflect, and only the execute step had
 * nowhere to happen: you plan on Today, you tick off on Today, and while actually working the
 * screen in front of you is the one with nine other things on it. Everything here already existed
 * on the task panel. The feature is what has been taken away.
 *
 * ## This is not a timer, and the copy says so
 *
 * §3 excludes time tracking and pomodoro. Nothing here is tracked and no interval is invented:
 * the end time is the block's own `endsAt`, the bar is the fraction of *that* which has passed,
 * and when it runs out the strip asks a question rather than starting anything. `nowBlock.ts`
 * carries the same reasoning for why the overrun state is a held id and not a window of minutes.
 * The sentence is in the markup as well as here so that the next reader does not have to reopen
 * the exclusion to find out whether it was considered.
 *
 * ## The state this screen is usually in
 *
 * Measured before it was built, on 2026-09-10: across all nine persona accounts on the reference
 * instance and 61 days each, there were 38 blocks and **none on today**. The ADHD account — the
 * one the acceptance criteria names, seeded with eleven `in_progress` tasks — has none on any of
 * those days. So `running` is the state this screen is named after and the rarest one it renders.
 * The empty and next-block paths are written first, given the same care, and say something useful
 * rather than apologising.
 *
 * ## The way in
 *
 * The `block-actions` row on Today, which already exists, and nothing else: the nav rehang is its
 * own task (`01a08883-c0b8`, blocked on this one) and a tab added here would collide with it.
 * That does mean an account with no blocks at all today cannot reach this screen, which on
 * today's data is every account — recorded on the tracker rather than papered over with a second
 * door this task was told not to build.
 */
export function Now({
  onBack,
  onOpenTask,
  /** The block Today was pointing at, so opening from a future block lands on that one. */
  fromBlockId,
}: {
  onBack: () => void;
  onOpenTask: (id: string) => void;
  fromBlockId: string | null;
}) {
  const queryClient = useQueryClient();
  const { timezone, today } = useAccountDay();
  const plan = useQuery({ queryKey: ['day', today], queryFn: () => getDayPlan(today) });

  /*
    The clock, once a minute — the same resolution and the same reasoning as Today's. A block's
    remaining minutes and the bar are both minute-grained, so a faster interval would re-render
    for the same pixel.
  */
  const [now, setNow] = useState(() => Date.now());
  /*
    When this screen opened.

    Two things read it, and they are the same fact. `focusNow` uses it to tell a block that ran
    out under the reader from one that was already over when they arrived — see `nowBlock.ts`.
    The captured count uses it as the line rows are counted after. A ref, so that a re-render
    cannot move either line.
  */
  const openedAt = useRef(Date.now());

  /*
    Tomorrow, for the rail alone.

    `GET /api/day/:date` selects blocks overlapping the account's calendar day, so a block that
    *starts* after local midnight is tomorrow's row and this screen never saw it. Late in the evening
    that is exactly when the next thing is on the other side of midnight, and the rail — whose whole
    job is "what am I forgetting" — answered "Nothing after this." Seen at 22:41 UTC on an account in
    UTC, with a block running and the next one at 00:11.

    Deliberately **not** widened in `/api/day/:date` itself. Today's timeline is a record of one day,
    and `eventsBetween`'s overlap rule is what keeps a block that runs past midnight on the day it
    started. A second read here costs one cached request and changes nothing else.

    Only fetched while the horizon below can actually reach tomorrow, so an ordinary daytime visit
    makes one request as before.
  */
  const tomorrow = shiftDay(today, 1);
  /*
    Does the horizon cross into another local day? Asked of `localDay` twice rather than by computing
    an end-of-day instant, so the account's zone and its DST are whatever `localDay` already says they
    are — this screen has no business owning a second answer to "when does today end".
  */
  const reachesTomorrow = useMemo(
    () => localDay(new Date(now), timezone) !== localDay(new Date(now + RAIL_HORIZON_MS), timezone),
    [timezone, now],
  );
  const next = useQuery({
    queryKey: ['day', tomorrow],
    queryFn: () => getDayPlan(tomorrow),
    enabled: reachesTomorrow,
  });
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 60_000);
    return () => clearInterval(id);
  }, []);

  /*
    Which block this screen is about.

    Seeded from the block Today was pointing at, then written by `N`. It is also what makes the
    overrun question possible without inventing an interval — see `nowBlock.ts`.
  */
  const [heldId, setHeldId] = useState<string | null>(fromBlockId);

  const blocks: NowBlock[] = useMemo(() => plan.data?.events ?? [], [plan.data]);
  /*
    The rail sees tomorrow; `focusNow` does not, and that asymmetry is the point.

    "Running now" is a claim about today's record — a block from tomorrow cannot be the one you are
    in — so widening `focusNow`'s input would let a midnight-adjacent block become the screen's
    subject. The rail is a question about the future and has no such restriction.

    Cut at `RAIL_HORIZON_MS` rather than merging the whole day, because without a horizon the rail at
    08:00 on an empty day would name tomorrow's 09:00 block: twenty-five hours away, which is not
    what "what am I forgetting" asks. Six hours is long enough to carry the reported case (forty
    minutes, at 23:30) and every evening like it, and short enough that nothing from tomorrow can
    surface before the evening it belongs to.
  */
  const railBlocks: NowBlock[] = useMemo(
    () => [
      ...blocks,
      ...(next.data?.events ?? []).filter(
        (b) => new Date(b.startsAt).getTime() - now <= RAIL_HORIZON_MS,
      ),
    ],
    [blocks, next.data, now],
  );
  /*
    `focusNow` chooses from today, and resolves a held block from either day.

    Those are two different uses of the list and only the first must stay today's. Its unheld
    fall-through picks the soonest block ahead when nothing is running, so handing it the merged list
    outright would make tomorrow's 00:10 the *subject* of a screen opened at 23:30 with today already
    over — a block from tomorrow cannot be the one you are in, which is the rule this screen is built
    on. But pressing `N` onto that block has to work, and `focusNow` can only hold what it can see.

    So the held block is added and nothing else: `focusNow` returns at its `held` branch before the
    fall-through is reached, and a block that starts in the future lands on `upcoming` — `next`, which
    is what the strip should say.
  */
  const heldFromNextDay = useMemo(
    () => railBlocks.find((b) => b.id === heldId && !blocks.some((t) => t.id === b.id)),
    [railBlocks, blocks, heldId],
  );
  const focus = focusNow({
    blocks: heldFromNextDay ? [...blocks, heldFromNextDay] : blocks,
    now,
    heldId,
    openedAt: openedAt.current,
  });
  const focused = focus.kind === 'none' ? null : focus.block;
  const rail = upNext({ blocks: railBlocks, now, excludeId: focused?.id ?? null }).slice(0, 2);

  /*
    Hold whatever is on screen, so a block that runs out under the reader keeps the screen.

    Without this, the strip would go from "12 minutes left" straight to the empty state at the
    moment the block ended — the one moment the person is definitely looking at it.
  */
  useEffect(() => {
    if (focused && focused.id !== heldId) setHeldId(focused.id);
  }, [focused, heldId]);

  const task: Task | null = focused?.taskId
    ? ((plan.data?.tasks ?? []).find((t) => t.id === focused.taskId) ?? null)
    : null;

  const steps = useQuery({
    queryKey: ['checklist', task?.id],
    queryFn: () => listChecklist(task!.id),
    enabled: task !== null,
  });
  /* The step you are on: the first that is not ticked. Null when the list is done, or empty. */
  const hereId = (steps.data ?? []).find((s) => s.doneAt === null)?.id ?? null;

  const goalTree = useQuery({ queryKey: ['goals', 'tree'], queryFn: getGoalTree });
  const goalChips = useMemo(() => {
    const colors = goalColorMap((goalTree.data ?? []) as never);
    const map = new Map<string, { title: string; colorClass: string }>();
    const walk = (nodes: { goal: { id: string; title: string }; children: unknown[] }[]) => {
      for (const node of nodes) {
        map.set(node.goal.id, {
          title: node.goal.title,
          colorClass: colors.get(node.goal.id) ?? '',
        });
        walk(node.children as typeof nodes);
      }
    };
    walk((goalTree.data ?? []) as never);
    return map;
  }, [goalTree.data]);

  /*
    What arrived while you were here, counted and not shown.

    Capture keeps working from the header on every screen, and the whole point of this one is that
    a new thought does not become a new thing on the screen. So the inbox is read, compared
    against the instant the screen opened, and reported as a number — the pile is quiet, and it is
    on Tasks when you want it. `openedAt` is a ref so that a re-render cannot move the line and
    make the count drift back to zero.
  */
  const inbox = useQuery({
    queryKey: ['tasks', 'inbox', 'now'],
    queryFn: () => listTasks({ status: 'inbox' }),
  });
  const capturedHere = (inbox.data ?? []).filter(
    (t) => Date.parse(t.createdAt) >= openedAt.current,
  ).length;

  const invalidate = () => queryClient.invalidateQueries();

  const tick = useMutation({
    mutationFn: (item: ChecklistItem) =>
      updateChecklistItem(item.taskId, item.id, { done: item.doneAt === null }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['checklist', task?.id] }),
  });
  /*
    The same service call the row's status control and the block bar make, so the audit row and
    the scope charge are the ones that already exist — and the block stays on the timeline
    afterwards, which is `markBlocksCompleted`'s doing and §4's rule: the timeline is a record of
    the day, not a to-do list.
  */
  const finish = useMutation({ mutationFn: completeTask, onSuccess: invalidate });
  const extend = useMutation({
    mutationFn: (args: { eventId: string; durationMinutes: number }) =>
      moveBlock(args.eventId, { durationMinutes: args.durationMinutes }),
    onSuccess: invalidate,
  });

  const spanMinutes = focused
    ? (Date.parse(focused.endsAt) - Date.parse(focused.startsAt)) / 60_000
    : 0;
  const busy = finish.isPending || extend.isPending || plan.isFetching;

  const addFifteen = () => {
    if (!focused || busy) return;
    extend.mutate({
      eventId: focused.id,
      durationMinutes: extendedMinutes(spanMinutes, 15),
    });
  };
  const complete = () => {
    if (!task || task.status === 'done' || busy) return;
    finish.mutate(task.id);
  };
  const tickHere = () => {
    const here = (steps.data ?? []).find((s) => s.id === hereId);
    if (here && !tick.isPending) tick.mutate(here);
  };
  /*
    `N` steps to whatever the rail is naming, which used to be a second computation of the same
    question over a narrower list.

    That duplication is how this screen came to have two halves of one defect: the rail was fixed to
    see past midnight and `N` would still have stopped at it, because this line recomputed `upNext`
    over today alone. One list, read once, so the key and the text cannot disagree about what is next.
  */
  const toNextBlock = () => {
    const after = rail[0];
    if (after) setHeldId(after.id);
  };

  /*
    The four keys, plus the one every screen owes.

    Declared in `keys.ts` under the `now` scope and bound here, which `useKeys` makes one act
    rather than two — see `keyScope.ts`, whose guard was written with this screen in mind.

    Escape layers the way `TaskView`'s does: it stands down on `defaultPrevented`, which is what
    `useEscapeReleasesFields` sets in the capture phase when a field had the keyboard, and on
    anything open over the page, so backing out of a menu does not also leave the screen.
  */
  useKeys('now', (e: KeyboardEvent) => {
    if (e.metaKey || e.ctrlKey || e.altKey) return;
    if (e.key === 'Escape') {
      if (e.defaultPrevented || hasOpenSurface()) return;
      return onBack();
    }
    if (isTypingTarget()) return;
    if (e.key === 'Enter') {
      e.preventDefault();
      return tickHere();
    }
    if (e.key === 'd' || e.key === 'D') {
      e.preventDefault();
      return complete();
    }
    if (e.key === '+' || e.key === '=') {
      e.preventDefault();
      return addFifteen();
    }
    if (e.key === 'n' || e.key === 'N') {
      e.preventDefault();
      return toNextBlock();
    }
  });

  const clock = (iso: string) =>
    new Date(iso).toLocaleTimeString([], {
      hour: '2-digit',
      minute: '2-digit',
      timeZone: timezone,
    });

  if (!plan.data) {
    return (
      <section className="now" data-testid="now">
        <NowHeader onBack={onBack} />
        <QueryState query={plan} what="today" />
      </section>
    );
  }

  return (
    <section className="now" data-testid="now">
      <NowHeader onBack={onBack} />

      {/*
        The strip. Four states, and three of them have no bar at all — a progress bar for a block
        that has not started would be a timer counting down to one, which is the thing §3 excludes.
      */}
      <div className="now-strip" data-testid="now-strip" data-state={focus.kind}>
        {focus.kind === 'running' && (
          <>
            <p className="now-when">
              <span data-testid="now-left">{focus.leftMinutes} min</span> left · ends{' '}
              {clock(focus.block.endsAt)}
            </p>
            <div
              className="now-elapsed"
              role="progressbar"
              aria-valuemin={0}
              aria-valuemax={100}
              aria-valuenow={Math.round(focus.elapsed * 100)}
              aria-label="How much of this block has passed"
            >
              <span style={{ width: `${Math.min(100, Math.round(focus.elapsed * 100))}%` }} />
            </div>
          </>
        )}
        {focus.kind === 'overrun' && (
          <p className="now-when" data-testid="now-question">
            {/* The two writes below, asked as a question. Nothing fires a notification: the
                person is looking at the screen, which is why this screen exists. */}
            That block ended {focus.overMinutes} min ago. Done, or fifteen more minutes?
          </p>
        )}
        {focus.kind === 'next' && (
          <p className="now-when" data-testid="now-starts-in">
            Starts in {focus.startsInMinutes} min · {clock(focus.block.startsAt)}
          </p>
        )}
        {focus.kind === 'none' && (
          <p className="now-when" data-testid="now-idle">
            Nothing is blocked out for the rest of today.
          </p>
        )}
      </div>

      {focused ? (
        <div className="now-body">
          <div className="now-main">
            {task ? (
              <>
                <h2 className="now-title">
                  <button className="link now-title-link" onClick={() => onOpenTask(task.id)}>
                    {task.title}
                  </button>
                </h2>
                <p className="now-chips">
                  {goalChipsFor(task, goalChips)
                    .slice(0, 1)
                    .map((chip) => (
                      <span
                        key={chip.title}
                        className={`pill goal-chip ${chip.colorClass}`}
                        title={`Goal: ${chip.title}`}
                      >
                        {chip.title}
                      </span>
                    ))}
                  {task.status === 'done' && (
                    <span className="pill" data-testid="now-done">
                      {STATUS_VERB.done.past}
                    </span>
                  )}
                </p>
                {/* The first paragraph only. The rest is on the task, one click away — a screen
                    whose value is subtraction cannot open with six paragraphs of markdown. */}
                {firstParagraph(task.descriptionMd) && (
                  <p className="now-note">{firstParagraph(task.descriptionMd)}</p>
                )}
                <Steps items={steps.data ?? []} hereId={hereId} onTick={(i) => tick.mutate(i)} />
              </>
            ) : (
              <>
                {/* A calendar entry with no task behind it — a meeting. It still answers "what am
                    I doing", which is the question; it just has no steps and nothing to complete. */}
                <h2 className="now-title">{focused.title}</h2>
                <p className="subtext">From your calendar. Nothing to tick off.</p>
              </>
            )}
          </div>

          <aside className="now-rail" data-testid="now-rail">
            <h3>Next</h3>
            {rail.length === 0 ? (
              <p className="subtext">Nothing after this.</p>
            ) : (
              <ul className="plain now-next">
                {rail.map((b) => (
                  <li key={b.id} className={b.taskId === null ? 'is-calendar' : ''}>
                    <span className="now-next-time">{clock(b.startsAt)}</span> {b.title}
                  </li>
                ))}
              </ul>
            )}
            <Habits habits={plan.data.habits} />
          </aside>
        </div>
      ) : (
        <div className="now-body">
          <div className="now-main">
            {/*
              The state every account on the reference instance is actually in. It names the one
              act that changes it and points at where that act happens, rather than being blank
              and calling itself calm.
            */}
            <h2 className="now-title">Nothing running</h2>
            <p className="now-note">
              This screen shows the block you are inside. Draw one on Today's timeline, or drag
              something across from the shortlist, and it will be here.
            </p>
            <button type="button" onClick={onBack}>
              Back to Today
            </button>
          </div>
          <aside className="now-rail" data-testid="now-rail">
            <h3>Next</h3>
            <p className="subtext">Nothing after this.</p>
            <Habits habits={plan.data.habits} />
          </aside>
        </div>
      )}

      {/*
        The keys, and on a phone the buttons that stand in for them.

        Rendered from the registry rather than typed into the markup — `keyScopesAreClaimed`
        forbids a hand-written kbd element on a screen that claims a scope, because that is how
        the goal tree ended up with two documents about itself disagreeing by three keys. Its
        check reads the source text, so it counts a mention in prose too; spelling the tag out
        here would fail it as surely as writing the letter `N` into the JSX would.
      */}
      <div className="now-acts" data-testid="now-acts">
        <button type="button" onClick={tickHere} disabled={hereId === null}>
          Tick this step
        </button>
        <button
          type="button"
          onClick={complete}
          disabled={task === null || task.status === 'done' || busy}
        >
          {STATUS_VERB.done.verb}
        </button>
        <button type="button" onClick={addFifteen} disabled={focused === null || busy}>
          15 more minutes
        </button>
        <button type="button" onClick={toNextBlock} disabled={rail.length === 0}>
          Next block
        </button>
      </div>
      <p className="subtext now-keys">
        {inlineKeys('now').map(([key, what]) => (
          <span key={key}>
            <kbd>{key}</kbd> {what.toLowerCase()}{' '}
          </span>
        ))}
      </p>

      <p className="subtext now-captured" data-testid="now-captured">
        {/* Counted, never listed. A thought captured while you are working must not become a
            thing on the screen you are working on. */}
        Captured while you were here: {capturedHere}
      </p>
      <p className="subtext now-scope">
        Nothing is being timed. The end time is the block's own — this app does not track hours.
      </p>
    </section>
  );
}

function NowHeader({ onBack }: { onBack: () => void }) {
  return (
    <button className="link now-back" onClick={onBack}>
      ← Today
    </button>
  );
}

/** The steps, with the one you are on marked. */
function Steps({
  items,
  hereId,
  onTick,
}: {
  items: ChecklistItem[];
  hereId: string | null;
  onTick: (item: ChecklistItem) => void;
}) {
  if (items.length === 0) return null;
  return (
    <ul className="plain now-steps" data-testid="now-steps">
      {items.map((item) => (
        <li
          key={item.id}
          className={item.id === hereId ? 'is-here' : item.doneAt ? 'is-done' : ''}
          data-testid={item.id === hereId ? 'now-here' : undefined}
        >
          <button type="button" className="link" onClick={() => onTick(item)}>
            <span aria-hidden="true">{item.doneAt ? '✓' : '○'}</span>{' '}
            <span className="now-step-label">{item.label}</span>
          </button>
          {item.id === hereId && <span className="pill now-here-pill">you are here</span>}
        </li>
      ))}
    </ul>
  );
}

/** Today's habits, read-only — the rail answers "what am I forgetting", not "log it now". */
function Habits({ habits }: { habits: DayHabit[] }) {
  const due = habits.filter((h) => h.period === 'day' || !h.onTrack);
  if (due.length === 0) return null;
  return (
    <>
      <h3>Also today</h3>
      <ul className="plain now-habits" data-testid="now-habits">
        {due.slice(0, 5).map((h) => (
          <li key={h.id} className={h.loggedOnDay > 0 ? 'is-done' : ''}>
            <span aria-hidden="true">{h.loggedOnDay > 0 ? '✓' : '○'}</span> {h.name}
          </li>
        ))}
      </ul>
    </>
  );
}

/**
 * The first paragraph of a description, or nothing.
 *
 * Blank-line separated, which is what markdown means by a paragraph, and trimmed so a description
 * that opens with a heading or a bullet does not render its own syntax as the sentence.
 */
function firstParagraph(md: string): string {
  const first =
    md
      .trim()
      .split(/\n\s*\n/)[0]
      ?.trim() ?? '';
  return first.startsWith('#') || first.startsWith('-') ? '' : first;
}
